<template>
    <div class="rate-card-container">
        <div :class="['rate-card row', { 'rep-example': ratesItem?.lender_rapr }]">
            <div class="col-12 p-0">
                <p class="lender-name" :class="{ 'credit-card': campaignType === 'Credit Card' }">
                    {{ ratesItem.lender_name }}
                </p>
            </div>

            <div class="lender-logo-wrapper--cc col-5 col-lg-3 order-1 d-flex flex-column align-self-center">
                <img :src="ratesItem.lender_logo" :alt="ratesItem.lender_name + ' lender-logo'" />

                <a v-if="hideCta" class="lender-disclosure-link flex-direction-column" @click="showOfferDetails">
                    {{ $t('ratesTable.lenderTsAndInfo') }}
                </a>
            </div>

            <div class="d-flex d-lg-none col-12 p-0 pt-3">
                <RatesTableTagContainer :ratesItem="ratesItem" />
            </div>

            <div class="col-7 col-lg-8 order-2 pl-4">
                <div class="row order-2 order-lg-1" ref="offers">
                    <div
                        class="table-value col-12 info-wrapper--generic order-lg-1 order-1 d-flex"
                        :class="columnNumber"
                    >
                        <RatesCardAmount :loanRange="ratesItem?.loan_range" :rangeLabel="$t('results.creditLimit')" />
                    </div>

                    <template
                        v-if="
                            checkOnlyCreditCardProductType('Purchase Card') ||
                            checkOnlyCreditCardProductType('Line Of Credit') ||
                            checkOnlyCreditCardProductType('Credit Builder') ||
                            checkOnlyCreditCardProductType('Rewards')
                        "
                    >
                        <!-- col 2 -->
                        <div
                            v-if="ratesItem?.purchases_term"
                            class="table-value col-12 info-wrapper--generic order-lg-2 order-2 d-flex"
                            :class="columnNumber"
                        >
                            <RatesCardSingleValue
                                :columnValue="ratesItem.purchases_term"
                                :appendingValueSymbol="' ' + ($t('results.months') ?? $t('results.offerBlocks.term'))"
                                :valueLabel="$t('results.offerBlocks.onPurchases')"
                            />
                        </div>

                        <!-- col 3 -->
                        <div
                            v-if="ratesItem?.apr"
                            class="table-value col-12 info-wrapper--generic order-lg-3 order-3 d-flex"
                            :class="columnNumber"
                        >
                            <RatesCardSingleValue
                                :columnValue="formatPercentage(ratesItem?.apr)"
                                :valueLabel="$t('results.offerBlocks.regularPurchasesApr', { feeType: feeType })"
                            />
                        </div>

                        <div
                            v-else-if="ratesItem?.apr_range"
                            class="table-value col-12 info-wrapper--generic order-lg-3 order-3 d-flex"
                            :class="columnNumber"
                        >
                            <RatesCardRangeGeneric
                                :rangeMin="ratesItem.apr_range?.min"
                                :rangeMax="ratesItem.apr_range?.max"
                                :rangeLabel="$t('results.offerBlocks.regularPurchasesApr', { feeType: feeType })"
                            />
                        </div>

                        <!-- col 4 -->
                        <div
                            class="table-value col-12 info-wrapper--generic order-lg-4 order-4 d-flex"
                            :class="columnNumber"
                        >
                            <RatesCardGeneric
                                :columnValue="
                                    ratesItem.fees ? formatCurrency(ratesItem.fees) : $t('results.filtering.noFees')
                                "
                                :valueLabel="feesDurationLabel"
                            />
                        </div>
                    </template>

                    <template v-else-if="checkOnlyCreditCardProductType('Balance Transfer')">
                        <!-- col 2 -->
                        <div
                            v-if="ratesItem.bt_term"
                            class="table-value col-12 info-wrapper--generic order-lg-2 order-2 d-flex"
                            :class="columnNumber"
                        >
                            <RatesCardSingleValue
                                :columnValue="ratesItem.bt_term"
                                :appendingValueSymbol="$t('results.months') ?? $t('results.offerBlocks.term')"
                                :valueLabel="$t('results.offerBlocks.onPurchases')"
                            />
                        </div>

                        <!-- col 3 -->
                        <div
                            v-if="ratesItem.bt_apr"
                            class="table-value col-12 info-wrapper--generic order-lg-3 order-3 d-flex"
                            :class="columnNumber"
                        >
                            <RatesCardSingleValue
                                :columnValue="formatPercentage(ratesItem.bt_apr)"
                                :valueLabel="$t('results.offerBlocks.balanceTransferApr', { feeType: feeType })"
                            />
                        </div>

                        <div
                            v-else-if="ratesItem?.apr_range"
                            class="table-value col-12 info-wrapper--generic order-lg-3 order-3 d-flex"
                            :class="columnNumber"
                        >
                            <RatesCardRangeGeneric
                                :rangeMin="ratesItem?.apr_range.min"
                                :rangeMax="ratesItem?.apr_range.max"
                                :rangeLabel="$t('results.offerBlocks.regularPurchasesApr', { feeType: feeType })"
                            />
                        </div>

                        <!-- col 4 -->
                        <div
                            class="table-value col-12 info-wrapper--generic order-lg-4 order-4 d-flex"
                            :class="columnNumber"
                        >
                            <RatesCardGeneric
                                :columnValue="
                                    ratesItem.fees ? formatCurrency(ratesItem.fees) : $t('results.filtering.noFees')
                                "
                                :valueLabel="feesDurationLabel"
                            />
                        </div>
                    </template>

                    <template
                        v-if="
                            (creditCardProductTypes.includes('Purchase Card') ||
                                creditCardProductTypes.includes('Rewards')) &&
                            creditCardProductTypes.includes('Balance Transfer')
                        "
                    >
                        <!-- col 2 -->
                        <div
                            v-if="ratesItem.bt_term"
                            class="table-value col-12 info-wrapper--generic order-lg-3 order-3 d-flex"
                            :class="columnNumber"
                        >
                            <RatesCardSingleValue
                                :columnValue="ratesItem.bt_term"
                                :appendingValueSymbol="$t('results.months') ?? $t('results.offerBlocks.term')"
                                :valueLabel="$t('results.offerBlocks.onPurchases')"
                            />
                        </div>

                        <!-- col 3 -->
                        <div
                            v-if="ratesItem?.purchases_term"
                            class="table-value col-12 info-wrapper--generic order-lg-3 order-3 d-flex"
                            :class="columnNumber"
                        >
                            <RatesCardSingleValue
                                :columnValue="ratesItem.purchases_term"
                                :appendingValueSymbol="' ' + ($t('results.months') ?? $t('results.offerBlocks.term'))"
                                :valueLabel="$t('results.offerBlocks.onPurchases')"
                            />
                        </div>

                        <!-- col 4 -->
                        <div
                            v-if="ratesItem?.apr"
                            class="table-value col-12 info-wrapper--generic order-lg-4 order-4 d-flex"
                            :class="columnNumber"
                        >
                            <RatesCardSingleValue
                                :columnValue="formatPercentage(ratesItem?.apr)"
                                :valueLabel="$t('results.offerBlocks.balanceTransferApr', { feeType: feeType })"
                            />
                        </div>

                        <div
                            v-else-if="ratesItem?.apr_range"
                            class="table-value col-12 info-wrapper--generic order-lg-4 order-4 d-flex"
                            :class="columnNumber"
                        >
                            <RatesCardRangeGeneric
                                :rangeMin="ratesItem?.apr_range.min"
                                :rangeMax="ratesItem?.apr_range.max"
                                :rangeLabel="$t('results.offerBlocks.regularPurchasesApr', { feeType: feeType })"
                            />
                        </div>
                    </template>
                </div>

                <div class="d-lg-flex d-none row order-1 order-lg-2">
                    <div class="table-value col-12 info-wrapper--generic order-lg-6 order-6 d-flex">
                        <RatesTableTagContainer :ratesItem="ratesItem" />
                    </div>
                </div>
            </div>

            <div v-if="!hideCta" class="lender-button-wrapper col order-5">
                <a
                    class="button secondary rates-table-button"
                    :class="{ 'credit-card': campaignType === 'Credit Card' }"
                    @click="ratesTableRedirect"
                >
                    {{ $t('form.general.continue') }}
                </a>

                <a
                    v-if="ratesItem?.terms_url?.terms_linktext"
                    :href="ratesItem?.terms_url.terms_hyperlink"
                    class="lender-disclosure-link"
                    target="_blank"
                >
                    {{ ratesItem.terms_url.terms_linktext }}
                </a>
            </div>

            <div v-if="ratesItem?.lender_rapr" class="representative-example col-12 order-6">
                <p>{{ $t('results.repExample') }}: <span v-html="ratesItem?.lender_rapr"></span></p>
            </div>
        </div>

        <FullScreenModal :name="modalName" :forceNoOverlay="true">
            <template v-slot:topHeading>
                <h2 class="offer-details-modal-title">
                    {{ $t('ratesTable.offerDetails') }}
                </h2>
            </template>

            <template v-slot>
                <h3 class="offer-details-subheading mt-3">{{ ratesItem.lender_name }}</h3>
                <p>{{ ratesItem.about_lender }}</p>

                <h4 class="offer-details-subheading">{{ $t('ratesTable.TermsAndConditions') }}</h4>
                <p>{{ ratesItem.terms }}</p>
            </template>
        </FullScreenModal>
    </div>
</template>

<script>
import { useStore } from 'vuex'
import { url } from '@js/utilities/url.js'
import { formatCurrency, formatPercentage } from '@js/utilities/number.js'
import lowerCase from 'lodash/lowerCase'
import isEmpty from 'lodash/isEmpty'
import floor from 'lodash/floor'

export default {
    props: {
        ratesItem: {},
        uid: null,
        campaignType: 'default',
    },
    setup() {
        const store = useStore()

        return {
            store,
        }
    },
    inject: ['isDynamicRatesTables'],
    data() {
        return {
            loading: false,
            errors: [],
            columnNumber: 'col-lg-3',
        }
    },
    mounted() {
        if (this.$refs.offers) {
            this.columnNumber = 'col-lg-' + floor(12 / this.$refs.offers.children.length)
        }
    },
    computed: {
        modalName() {
            return this.ratesItem.lender_name + this.uid
        },
        hideCta: function () {
            return this.store?.state?.global?.page?.hide_cta
        },
        feeType() {
            return this.ratesItem.fee_type && this.ratesItem.fee_type !== 'N/A'
                ? lowerCase(this.ratesItem.fee_type)
                : 'variable'
        },
        feesDurationLabel() {
            const feesDuration = this.ratesItem?.fees_duration ? lowerCase(this.ratesItem.fees_duration) : null

            switch (feesDuration) {
                case 'annually':
                    return this.$t('results.annualFees')
                case 'monthly':
                    return this.$t('results.monthlyFees')
                case 'weekly':
                    return this.$t('results.weeklyFees')
                case 'daily':
                    return this.$t('results.dailyFees')
            }

            return this.$t('results.annualFees')
        },
        creditCardProductTypes() {
            const productType = this.offer?.card_type
            if (productType && productType.length > 0) {
                return productType.split(',')
            }
            return ['Purchase Card']
        },
    },

    methods: {
        formatCurrency: formatCurrency,
        formatPercentage: formatPercentage,
        isEmpty,
        showOfferDetails: function () {
            this.emitter.emit(this.modalName, {
                open: true,
            })
        },
        ratesTableRedirect: function () {
            if (this.isDynamicRatesTables) {
                this.trackEvent('event', {
                    eventCategory: 'button',
                    eventAction: 'click',
                    eventLabel: 'check-my-rate',
                    eventValue: this.ratesItem.lender_name,
                })

                const location = this.$router.resolve({
                    name: 'apply',
                })

                return (window.location = url(location.href))
            }

            return window.open(this.ratesItem.redirect_url, '_blank')
        },
        checkOnlyCreditCardProductType(item) {
            if (this.creditCardProductTypes.length === 0) {
                this.creditCardProductTypes[0] = 'Purchase Card'
            }
            return this.creditCardProductTypes.length === 1 && this.creditCardProductTypes[0] === item
        },
    },
}
</script>

<style lang="scss">
@import '~scss/settings/responsive';
@import '~scss/mixins/rem';

.lender-info-value,
.lender-info-label {
    text-align: left !important;
}
</style>
